/**
 * useGlobalSearch — unified offline-first global search hook.
 *
 * Public API for any component that needs search results:
 *   - Corpus is pre-loaded via useSearchCorpus (memory + Dexie + SWR)
 *   - Debounce (220 ms) handled internally — no per-keystroke network calls
 *   - All filtering is purely local against a pre-computed token index
 *   - Returns normalized SearchResult[] grouped by entity type
 *
 * Normalized item shape:
 *   { id, type, title, subtitle, route, searchableText, updated_at,
 *     recent, Icon, iconBg, iconColor }
 *
 * @param {{ districtId: string, query: string }} params
 * @returns {{ sections, totalResults, loading, debouncedQuery }}
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { expandQuery } from "@/lib/search-synonyms";
import { buildIndex, sortIndexed, unionSortIndexed } from "../lib/search-index";
// Side-effect: registers all providers into the registry
import { registry } from "../providers";
import { useSearchCorpus } from "./useSearchCorpus";

// Providers whose queries are expanded with synonyms before ranking
const SYNONYM_PROVIDERS = new Set(["visitas", "oraciones"]);

function normalizeItem(raw, provider) {
  const built = provider.buildItem(raw);
  return {
    id: built.id ?? raw.id,
    type: provider.id,
    title: built.title ?? "",
    subtitle: built.subtitle ?? "",
    route: built.route ?? "",
    searchableText: provider.fields.map((f) => (raw[f] ? String(raw[f]) : "")).filter(Boolean).join(" "),
    updated_at: raw.updated_at || raw.created_at || null,
    recent: built.recent ?? null,
    Icon: provider.icon,
    iconBg: provider.iconBg,
    iconColor: provider.iconColor,
  };
}

export function useGlobalSearch({ districtId, query: rawQuery = "" }) {
  const { rawCorpus, loading } = useSearchCorpus(districtId);

  // Debounce: 220 ms — matches the existing modal debounce timing
  const [debouncedQuery, setDebouncedQuery] = useState(rawQuery);
  const timerRef = useRef(null);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebouncedQuery(rawQuery), 220);
    return () => clearTimeout(timerRef.current);
  }, [rawQuery]);

  // Build pre-computed token index from corpus.
  // Rebuilds only when rawCorpus reference changes (after SWR refresh), never on keystrokes.
  const indexedData = useMemo(() => {
    if (!rawCorpus) return {};
    const result = {};
    for (const p of registry.getAll()) {
      result[p.id] = buildIndex(rawCorpus[p.id] || [], p.fields);
    }
    return result;
  }, [rawCorpus]);

  const q = debouncedQuery.trim();

  // Per-type local filtering — zero network, purely in-memory index scan.
  // sections[] is stable between keystrokes when indexedData hasn't changed.
  const sections = useMemo(() => {
    if (!q) return [];
    const result = [];
    for (const p of registry.getAll()) {
      const idx = indexedData[p.id];
      if (!idx?.length) continue;
      const hits = SYNONYM_PROVIDERS.has(p.id)
        ? unionSortIndexed(idx, expandQuery(q))
        : sortIndexed(idx, q);
      if (hits.length === 0) continue;
      result.push({
        key: p.id,
        label: p.label,
        icon: p.icon,
        iconBg: p.iconBg,
        iconColor: p.iconColor,
        items: hits.map((raw) => normalizeItem(raw, p)),
      });
    }
    return result;
  }, [indexedData, q]);

  const totalResults = useMemo(
    () => sections.reduce((sum, s) => sum + s.items.length, 0),
    [sections]
  );

  return { sections, totalResults, loading, debouncedQuery };
}
